import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RilievoDTO } from '@/core/models/models/pages/rilevamento';
import { SCAMBIO } from '@/core/models/models/dominio/util.model';
import { TELAI_KEYS } from './config-constants';
import { buildTelaioFromForm, flattenTelaio } from './config-helper';

/**
 * Aggiunge controlli dinamici al form se non presenti.
 */
export function addManyControls(form: FormGroup, fb: FormBuilder, names: string[]): void {
    for (const n of names) {
        if (!form.contains(n)) {
            const ctrl = fb.control(null, Validators.nullValidator);
            form.addControl(n, ctrl);
            ctrl.updateValueAndValidity({ emitEvent: false });
        }
    }
}

/**
 * Costruisce tutti i telai in base al tipo di scambio.
 */
export function buildTelai(formValue: any, tipoScambio: SCAMBIO) {
    let telaio1 = null,
        telaio2 = null,
        telaio3 = null,
        telaio4 = null,
        cuore = null;

    if (tipoScambio === SCAMBIO.SEMPLICE) {
        telaio1 = buildTelaioFromForm(formValue, '', TELAI_KEYS.SS);
    }

    if (tipoScambio === SCAMBIO.DOPPIO) {
        telaio1 = buildTelaioFromForm(formValue, 'ramo1_', TELAI_KEYS.SD_RAMO);
        telaio2 = buildTelaioFromForm(formValue, 'ramo2_', TELAI_KEYS.SD_RAMO);
        cuore = buildTelaioFromForm(formValue, 'cuore_', TELAI_KEYS.SD_CUORE);
    }

    if (tipoScambio === SCAMBIO.INTERSEZIONE) {
        telaio1 = buildTelaioFromForm(formValue, 'telaio1_', TELAI_KEYS.SI_T1);
        telaio2 = buildTelaioFromForm(formValue, 'telaio2_', TELAI_KEYS.SI_T2);
        telaio3 = buildTelaioFromForm(formValue, 'telaio3_', TELAI_KEYS.SI_T3);
        telaio4 = buildTelaioFromForm(formValue, 'telaio4_', TELAI_KEYS.SI_T4);
    }

    return { telaio1, telaio2, telaio3, telaio4, cuore };
}

/**
 * Converte un DTO di rilievo in patch per il form.
 */
export function mapRilievoToForm(r: RilievoDTO, tipo: number, tecnici: { label: string; value: any }[], strumenti: { label: string; value: any }[], fb: FormBuilder): any {
    const iv = r?.scambioSempliceIv ?? r?.scambioDoppioIv ?? r?.scambioIntersDoppio ?? null;

    const patch: any = {
        tecnico: r?.tecnicoRilevatore ? (tecnici.find((t) => t.value === r.tecnicoRilevatore.id) ?? null) : null,
        strumentoUtilizzato: r?.strumentoRilevatore ? (strumenti.find((s) => s.value === r.strumentoRilevatore.id) ?? null) : null
    };

    // lista interventi
    patch.interventi = fb.array(
        (r.listaInterventi && r.listaInterventi.length ? r.listaInterventi : [{ existFile: false }]).map((iv) =>
            fb.group({
                id: [iv.id ?? null],
                noteInterventoRilevatore: [iv.noteInterventoRilevatore ?? null],
                tecnicoRilevatoreId: [iv.tecnicoRilevatore?.id ?? null],
                deviatoiRiepilogoGeneraleId: [iv.deviatoiRiepilogoGenerale?.id ?? null],
                pathFile: [iv.pathFile ?? null],
                foto: [iv.foto ?? null],
                existFile: [iv.existFile ? true : false]
            })
        )
    );

    // altri valori IV (escludendo il dataRilevamento interno)
    if (iv) {
        const { dataRilevamento: _, ...ivRest } = iv;
        Object.assign(patch, ivRest);
    }

    switch (tipo) {
        case 1:
            Object.assign(patch, flattenTelaio('', r?.telaio1, TELAI_KEYS.SS));
            break;
        case 2:
            Object.assign(patch, flattenTelaio('ramo1_', r?.telaio1, TELAI_KEYS.SD_RAMO), flattenTelaio('ramo2_', r?.telaio2, TELAI_KEYS.SD_RAMO), flattenTelaio('cuore_', r?.cuore, TELAI_KEYS.SD_CUORE));
            break;
        case 3:
            Object.assign(
                patch,
                flattenTelaio('telaio1_', r?.telaio1, TELAI_KEYS.SI_T1),
                flattenTelaio('telaio2_', r?.telaio2, TELAI_KEYS.SI_T2),
                flattenTelaio('telaio3_', r?.telaio3, TELAI_KEYS.SI_T3),
                flattenTelaio('telaio4_', r?.telaio4, TELAI_KEYS.SI_T4)
            );
            break;
        default:
            Object.assign(patch, flattenTelaio('', r?.telaio1, TELAI_KEYS.SS));
    }

    // Impostiamo dataRilevamento ALLA FINE per assicurarci che non venga sovrascritto
    patch.dataRilevamento = r?.dataRilevamento ? new Date(r.dataRilevamento) : null;

    return patch;
}
